import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '@environments/environment';
import { StaffService } from '@extensions/assignment/services/staff.service';
import { WorkService } from '@extensions/assignment/services/work.service';
import { ConfirmComponent } from '@shared/dialogs/confirm/confirm.component';
import { DateRangeComponent, IDateRangeInput } from '@shared/dialogs/date-range/date-range.component';
import { EvGroupSelectComponent } from '@shared/dialogs/ev-group-select/ev-group-select.component';
import { ProjectUploadComponent, IProjectUploadData } from '@shared/dialogs/project-upload/project-upload.component';
import { ActivitiesService } from '@shared/services/activities.service';
import { AdminService } from '@shared/services/admin.service';
import { ProjectService } from '@shared/services/project.service';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { INotificationEmail } from '@static/models/notification';
import { EPermissionLevel } from '@static/models/permissions';
import { IProject, IProjectListData, IProjectSettings } from '@static/models/project';
import { DatetimeDatePipe } from '@static/pipes/datetime-date.pipe';
import { AuthService } from '@static/services/auth.service';
import { HttpService } from '@static/services/http.service';
import { ObservableService } from '@static/services/observable.service';
import { SnackService } from '@static/services/snack.service';
import { TEMPLATE_CUSTOMER_NOTIFICATION } from '@static/templates/customer.email';
import { TEMPLATE_KPA_NOTIFICATION } from '@static/templates/kpa.email';
import moment from 'moment';

@Component({
  selector: 'app-info-project',
  imports: [
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatButtonModule,
    ReactiveFormsModule,
    DatetimeDatePipe,
  ],
  templateUrl: './info-project.component.html',
  styleUrl: './info-project.component.scss',
})
export class InfoProjectComponent implements OnChanges {
  private _observableService: ObservableService = inject(ObservableService);
  private _projectService: ProjectService = inject(ProjectService);
  private _staffService: StaffService = inject(StaffService);
  private _dialog: MatDialog = inject(MatDialog);
  private _authService: AuthService = inject(AuthService);
  private _activitiesService: ActivitiesService = inject(ActivitiesService);
  private _workService: WorkService = inject(WorkService);
  private _httpService: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);
  private _adminService: AdminService = inject(AdminService);

  @Input() projectInfo!: IProject;
  @Input() projectTitle!: string;
  @Input() accessLevel!: number;
  @Input() hasNotificationsOn!: boolean;
  @Input() hasProjectOwner!: boolean;
  @Input() extAssignment!: boolean;
  @Input() extExpense!: boolean;
  @Input() extChanges!: boolean;
  @Input() isSiteAdmin!: boolean;
  @Input() isGlobalAdmin!: boolean;
  @Input() lockedState!: boolean;
  @Input() isArchived!: boolean;
  @Input() canLock!: boolean;

  public PERMISSION_LEVEL = EPermissionLevel;
  private _datePipe = new DatePipe('en-AU');

  public projectUploadControl: FormControl;
  public staffUploadControl: FormControl;

  protected startDate?: string;
  protected endDate?: string;
  protected dataDate?: string;

  constructor() {
    this.projectUploadControl = new FormControl();
    this.staffUploadControl = new FormControl();
  }

  ngOnChanges(): void {
    if (this.projectInfo !== undefined) {
      this.startDate = this._projectService.getProjectStartDate(this.projectInfo.proj_id);
      this.endDate = this._projectService.getProjectEndDate(this.projectInfo.proj_id);
      this.dataDate = this._projectService.getProjectDataDate(this.projectInfo.proj_id);
    }
  }

  /**
   * Upload a staffing file which details contractors and supervisors in a known format
   *
   * @param event file upload event
   */
  public staffFileUpload(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files === null || !files.length) return;

    this._staffService.staffFileUpload(files[0]);
    this.staffUploadControl.reset();
  }

  /**
   * Upload a project file for processing
   *
   * @param event
   * @returns
   */
  public projectFileUpload(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;

    // Extract and build form
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('user', this._authService.email ?? '');

    // Post and wait for upload
    const dialog = this._dialog.open(ProjectUploadComponent, {
      disableClose: true,
      data: {
        formData,
        dataCallback: this._appendProjects.bind(this)
      } as IProjectUploadData<IProjectListData>
    });
    dialog.afterClosed().subscribe(() => {
      this.projectUploadControl.reset();
    });
  }


  /**
   * Append the new projects to the project list
   *
   * @param res projects to add
   */
  private _appendProjects(res: IProjectListData): void {
    this._projectService.appendProjects(res.projectInfo);
    this._projectService.appendProjectSettings(res.projectSettings);

    this._projectService.getCurrentInfo().then(() => {
      this._activitiesService.getWBSTaskStructure();
    });
  }

  /**
   * Toggle the project lock
   */
  public toggleProjectLock(): void {
    if (this.projectInfo) {
      const newState = (this.projectInfo.lock ?? 0) ? 0 : 1;
      this._projectService.toggleLock(this.projectInfo.proj_id, newState).then(() => {
        this.projectInfo!.lock = newState;
      });
    }
  }

  /**
   * Notify KPA that changes are complete
   */
  public notifyComplete(): void {
    this._httpService.getP<IProjectSettings>(`${environment.backendUri}/project/${this._projectService.currentId}/settings`)
      .then((res: IProjectSettings) => {
        const lastUpdate = res.last_kpa_notification;
        const confirm = this._dialog.open(
          ConfirmComponent,
          {data: `Notify the project manager that you have finished edits and work assignments for this period.<br><br>
            Last Notified: ${lastUpdate ? this._datePipe.transform(lastUpdate, 'hh:mm aa dd/MM/yyyy') : 'Never'} ` }
        );
        this._observableService.subscribe(
          confirm.afterClosed(),
          (confirmed: boolean) => {
            if (confirmed) {
              const uri = `${window.location.protocol}//${window.location.hostname}/${this.projectInfo.proj_id}`;
              const replacedTemplate = TEMPLATE_KPA_NOTIFICATION
                .replaceAll("%PROJECT%", this.projectInfo.proj_short_name ?? 'unknown project')
                .replaceAll("%USER%", this._authService.username ?? 'Unknown')
                .replaceAll("%ADDRESS%", uri);

              const reqDate = {
                subject: `Ctrl key Updates Complete ${this.projectInfo.proj_short_name}`,
                sender: this._authService.email,
                recipients: res.kpa_contact_email.replace(',', ';'),
                body: replacedTemplate,
              } as INotificationEmail;

              // Notify and lock
              this._httpService.postP(environment.notificationUri, reqDate)
                .then(() => {
                  const uri = `${environment.backendUri}/project/${this._projectService.currentId}/settings/kpanotify`;
                  this._httpService.postP(uri)
                    .then(() => {
                      this._snackService.openInfoSnack('Successfully notified project manager');
                    }).catch((err: HttpErrorResponse) =>
                      this._snackService.openErrorSnack('Failed to record notification', err)
                    );
                }).catch((err: HttpErrorResponse) => this._snackService.openErrorSnack('Failed to send notification', err));
            }
          }
        );
      }).catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to load project settings', err);
      });
  }

  /**
   * Notify the Customer that changes are complete
   */
  public notifyCustomer(): void {
    this._httpService.getP<IProjectSettings>(`${environment.backendUri}/project/${this._projectService.currentId}/settings`)
      .then((res: IProjectSettings) => {
        const lastUpdate = res.last_customer_notification;
        const confirm = this._dialog.open(
          ConfirmComponent,
          {data: `This will send an email to all external users who have notifications enabled in the project settings.<br><br>
            Last Notified: ${lastUpdate ? this._datePipe.transform(lastUpdate, 'hh:mm aa dd/MM/yyyy') : 'Never'} ` }
        );
        this._observableService.subscribe(
          confirm.afterClosed(),
          (confirmed: boolean) => {
            if (confirmed) {
              const uri = `${window.location.protocol}//${window.location.hostname}/${this.projectInfo.proj_id}`;
              const replacedTemplate = TEMPLATE_CUSTOMER_NOTIFICATION
                .replaceAll("%PROJECT%", this.projectInfo.proj_short_name ?? 'unknown project')
                .replaceAll("%ADDRESS%", uri);

              this._projectService.getRBACRules().then((rules) => {
                const reqDate = {
                  subject: `Ctrl key Updated ${this.projectInfo.proj_short_name}`,
                  sender: this._authService.email,
                  recipients: rules.filter((rule) => rule.notification && rule.access_level > EPermissionLevel.NONE)
                    .map((rule) => rule.user_email ?? '').join(';'),
                  body: replacedTemplate,
                } as INotificationEmail;

                // Notify
                this._httpService.postP(environment.notificationUri, reqDate)
                  .then(() => {
                    this._httpService.postP(`${environment.backendUri}/project/${this._projectService.currentId}/settings`, {
                      key: 'last_customer_notification',
                      value: moment.utc().valueOf(),
                      updated_by: this._authService.email,
                      updated_time: moment.utc().valueOf()
                    })
                      .then(() => {
                        this._snackService.openInfoSnack('Successfully notified clients');
                      }).catch((err: HttpErrorResponse) =>
                        this._snackService.openErrorSnack('Failed to record customer notification', err)
                      );
                  }).catch((err: HttpErrorResponse) =>
                    this._snackService.openErrorSnack('Failed to send customer notification', err)
                  );
              });
            }
          }
        );
      }).catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to load project settings', err);
      });
  }

  /**
   * Trigger the export for activities with updated percentage complete
   */
  public exportChangedPercentComplete(): void {
    this._activitiesService.exportChangedPercentComplete();
  }

  /**
   * Trigger the export for work assignments in the UGL format
   */
  public exportAssignedWork(): void {
    const endDate = moment.utc(this._projectService.getProjectDataDate(this.projectInfo.proj_id))
      .add(this._projectService.getProjectUpdateFrequency(this.projectInfo.proj_id) - 1, 'd');;
    const confirm = this._dialog.open(DateRangeComponent, { data: {
      title: `Select the date range for the Assigned Work Export`,
      showGroups: true,
      start: this._projectService.getProjectDataDate(this.projectInfo.proj_id),
      end: endDate,
      periodDuration: this._projectService.getProjectUpdateFrequency(this.projectInfo.proj_id),
      periodKey: 'd',
    } as IDateRangeInput });
    this._observableService.subscribe(
      confirm.afterClosed(),
      (data?: [number, number, string]) => {
        if (data === undefined) return;
        this._workService.exportAssignedWork(data[0], data[1], data[2]);
      }
    )
  }

  /**
   * Export the budget hours
   */
  public exportBudgetHours(): void {
    this._activitiesService.exportBudgetHours();
  }

  /**
   * Trigger the export for all activities in the current filter configuration
   */
  public exportWBS(): void {
    this._activitiesService.exportWBSTaskStructure();
  }

  /**
   * Trigger the export for activities with no updated percentage complete but have work assigned
   */
  public exportNoPercentageUpdate(): void {
    this._activitiesService.exportNoPercentageUpdate();
  }

  /**
   * Export the user access list
   */
  public exportUserAccess(): void {
    this._adminService.exportProjectUserAccessList();
  }

  /**
   * Export list of comments
   */
  public exportComments(): void {
    this._activitiesService.exportComments();
  }

  /**
   * Export the list of work assignments with no underlying task
   */
  public exportMissingTasks(): void {
    this._activitiesService.exportMissingTasks();
  }

  /**
   * Export the earned value
   */
  public exportEarnedValue(): void {
    const confirm = this._dialog.open(EvGroupSelectComponent);
    this._observableService.subscribe(
      confirm.afterClosed(),
      (groupId?: number) => {
        if (groupId === undefined) return;
        this._activitiesService.exportEarnedValue(groupId);
      }
    )
  }
}
